import React, { useState, useEffect } from 'react'
import { Card, Tabs, Modal, message, Spin, List, Button, Tag, Typography, Row, Col, Space, Input, Select } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  BossGlobalStats,
  BossHeaderCard,
  BossOverviewTab,
  BossProgressTab,
  BossReceiptDetailModal,
  BossReceiptListModal,
  BossVerificationTab,
  BossVouchersTab
} from './boss/BossSections'
import { DataComparison, GlobalStats, ReceiptCheckSummary, ReceiptMismatch, RecentActivity, RoleProgress } from './boss/bossTypes'
dayjs.extend(relativeTime)
const { Text } = Typography

const BossPage: React.FC = () => {
  const [activeView, setActiveView] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    totalRevenue: 0,
    pendingAmount: 0,
    activeWorkflows: 0,
    completedToday: 0
  })
  const [roleProgress, setRoleProgress] = useState<RoleProgress>({
    vehicleManager: { pending: 0, completed: 0, overdue: 0 },
    accountant: { pending: 0, completed: 0, vouchers: 0 },
    cashier: { pending: 0, completed: 0, verified: 0 }
  })
  const [dataComparison, setDataComparison] = useState<DataComparison>({
    vehicleVsAccounting: 'match',
    accountingVsCashier: 'match',
    inconsistencies: []
  })
  const [receiptChecks, setReceiptChecks] = useState<ReceiptCheckSummary>({
    total: 0,
    mismatches: []
  })
  const [receiptMap, setReceiptMap] = useState<Record<string, any>>({})
  const [receiptDetailVisible, setReceiptDetailVisible] = useState(false)
  const [receiptDetail, setReceiptDetail] = useState<any>(null)
  const [receiptListVisible, setReceiptListVisible] = useState(false)
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([])
  const [allReceipts, setAllReceipts] = useState<any[]>([])
  const [rentAdjustRequests, setRentAdjustRequests] = useState<any[]>([])
  const [operationLogs, setOperationLogs] = useState<any[]>([])
  const [logKeyword, setLogKeyword] = useState('')
  const [logActionFilter, setLogActionFilter] = useState<'all' | 'create' | 'update' | 'delete'>('all')
  const [logCollectionFilter, setLogCollectionFilter] = useState<string>('all')
  
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { vehicles } = useVehicleStore()

  useEffect(() => {
    loadAllData()
  }, [])

  const loadAllData = async () => {
    try {
      setLoading(true)

      const [receiptsResult, adjustmentsResult, rentAdjustResult, operationLogsResult] = await Promise.all([
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments(),
        simpleDirectCloudApi.getRentAdjustRequests(),
        simpleDirectCloudApi.getOperationLogs(1000)
      ])
      const receipts = receiptsResult.success && receiptsResult.data ? receiptsResult.data : []
      setAllReceipts(receipts)
      const adjustments = adjustmentsResult.success && adjustmentsResult.data ? adjustmentsResult.data : []
      const adjustmentsClean = adjustments.filter((adj: any) => adj.type !== 'rent_addition')
      const rentAdjustRaw = rentAdjustResult.success && rentAdjustResult.data ? rentAdjustResult.data : []
      setRentAdjustRequests(rentAdjustRaw)
      setOperationLogs(operationLogsResult.success && operationLogsResult.data ? operationLogsResult.data : [])

      const paidStatuses = ['cashier_done', 'accounted', 'completed']
      const pendingReceipts = receipts.filter((r: any) => r.status === 'submitted')
      const paidReceipts = receipts.filter((r: any) => paidStatuses.includes(r.status))
      const accountedReceipts = receipts.filter((r: any) => r.status === 'accounted')

      const toNumber = (value: any) => {
        const num = Number(value)
        if (Number.isFinite(num)) return num
        const parsed = parseFloat(String(value ?? ''))
        return Number.isFinite(parsed) ? parsed : 0
      }
      const sumAmount = (r: any) => {
        const items = Array.isArray(r.items) ? r.items : []
        const itemsSum = items.reduce((sum: number, it: any) =>
          sum + toNumber(it?.receivedAmount ?? it?.expectedAmount ?? it?.amount ?? 0), 0)
        return toNumber(r.amount ?? itemsSum ?? 0)
      }

      const storedOutstandingRaw = localStorage.getItem('jxc_total_outstanding')
      const storedOutstanding = storedOutstandingRaw ? Number(storedOutstandingRaw) : NaN
      const pendingAmount = Number.isFinite(storedOutstanding)
        ? storedOutstanding
        : pendingReceipts.reduce((sum: number, r: any) => sum + sumAmount(r), 0)
      const adjustmentsDelta = adjustmentsClean.reduce((sum: number, adj: any) => sum + (Number(adj.amount || 0) || 0), 0) * -1
      const totalRevenue = paidReceipts.reduce((sum: number, r: any) => sum + sumAmount(r), 0) + adjustmentsDelta
      const activeReceipts = receipts.filter((r: any) => !['accounted', 'completed'].includes(r.status)).length

      const today = new Date().toDateString()
      const completedToday = receipts.filter((r: any) => {
        const t = r.accountedAt || r.verifiedAt || r.updatedAt || r.createdAt
        if (!t) return false
        return new Date(t).toDateString() === today && ['accounted', 'completed'].includes(r.status)
      }).length

      const newGlobalStats: GlobalStats = {
        totalRevenue,
        pendingAmount,
        activeWorkflows: activeReceipts,
        completedToday
      }

      const newRoleProgress: RoleProgress = {
        vehicleManager: {
          pending: pendingReceipts.length,
          completed: paidReceipts.length,
          overdue: calculateOverdue()
        },
        accountant: {
          pending: receipts.filter((r: any) => r.status === 'cashier_done').length,
          completed: accountedReceipts.length,
          vouchers: receipts.filter((r: any) => r.voucherData?.voucherNo || r.status === 'accounted').length
        },
        cashier: {
          pending: pendingReceipts.length,
          completed: paidReceipts.length,
          verified: receipts.filter((r: any) => r.status === 'cashier_done').length
        }
      }

      const newDataComparison = performDataVerification(newRoleProgress)
      const newReceiptChecks = buildReceiptChecks(receipts)
      const newReceiptMap = receipts.reduce((acc: Record<string, any>, r: any) => {
        const id = r._id || r.id
        if (id) acc[id] = r
        return acc
      }, {})

      const newRecentActivities = generateReceiptActivities(receipts)

      setGlobalStats(newGlobalStats)
      setRoleProgress(newRoleProgress)
      setDataComparison(newDataComparison)
      setReceiptChecks(newReceiptChecks)
      setReceiptMap(newReceiptMap)
      setRecentActivities(newRecentActivities)

      console.log('已从收据加载Boss数据')
    } catch (error) {
      console.error('Boss load error:', error)
      message.error('加载失败')
    } finally {
      setLoading(false)
    }
  }
  const calculateOverdue = (): number => {
    const now = new Date()
    return vehicles.filter(car => {
      if (!car.dueDate) return false
      const dueDate = new Date(car.dueDate)
      return dueDate < now
    }).length
  }

  const sumReceiptItems = (items: any[]) =>
    items.reduce((sum: number, it: any) => sum + Number(it?.receivedAmount ?? it?.expectedAmount ?? it?.amount ?? 0), 0)

  const buildReceiptChecks = (receipts: any[]): ReceiptCheckSummary => {
    const mismatches: ReceiptMismatch[] = []
    receipts.forEach(r => {
      const receiptId = r._id || r.id || 'unknown'
      const plate = r.plate
      const company = r.company || r.customerName
      const items = Array.isArray(r.items) ? r.items : []
      const itemsSum = sumReceiptItems(items)
      const receiptAmount = Number(r.amount ?? itemsSum ?? 0)

      if (items.length && Math.abs(itemsSum - receiptAmount) > 0.01) {
        mismatches.push({
          receiptId,
          plate,
          company,
          type: 'receipt_item_total_mismatch',
          description: `收据金额 ${receiptAmount.toFixed(2)} 与明细合计 ${itemsSum.toFixed(2)} 不一致`,
          severity: 'medium'
        })
      }

      if ((r.status === 'cashier_done' || r.status === 'accounted' || r.status === 'completed') && items.length) {
        const hasSignature = Boolean(r.cashierSignatureUrl || items.some((it: any) => it.cashierSignatureUrl))
        if (!hasSignature) {
          mismatches.push({
            receiptId,
            plate,
            company,
            type: 'missing_cashier_signature',
            description: '缺少出纳签名',
            severity: 'medium'
          })
        }
      }

      if (items.some((it: any) => !it.chargeRangeStart || !it.chargeRangeEnd)) {
        mismatches.push({
          receiptId,
          plate,
          company,
          type: 'missing_charge_range',
          description: '明细缺少扣款范围',
          severity: 'low'
        })
      }

      if (r.status === 'accounted' && r.voucherData) {
        const lines = r.voucherData.lines || []
        const totalDebit = r.voucherData.totalDebit ?? lines.reduce((s: number, l: any) => s + Number(l.debit || 0), 0)
        const totalCredit = r.voucherData.totalCredit ?? lines.reduce((s: number, l: any) => s + Number(l.credit || 0), 0)
        if (Math.abs(Number(totalDebit) - Number(totalCredit)) > 0.01) {
          mismatches.push({
            receiptId,
            plate,
            company,
            type: 'voucher_not_balanced',
            description: `凭证借方 ${Number(totalDebit).toFixed(2)} 与贷方 ${Number(totalCredit).toFixed(2)} 不一致`,
            severity: 'high'
          })
        }
        if (Math.abs(Number(totalCredit) - receiptAmount) > 0.01) {
          mismatches.push({
            receiptId,
            plate,
            company,
            type: 'voucher_receipt_amount_mismatch',
            description: `凭证贷方 ${Number(totalCredit).toFixed(2)} 与收据金额 ${receiptAmount.toFixed(2)} 不一致`,
            severity: 'high'
          })
        }
      }
    })

    return { total: receipts.length, mismatches }
  }

  const openReceiptDetail = (receiptId: string) => {
    const detail = receiptMap[receiptId]
    setReceiptDetail(detail || null)
    setReceiptListVisible(false)
    setReceiptDetailVisible(true)
  }

  const openReceiptList = () => {
    setReceiptListVisible(true)
  }

  const formatActivityTime = (value?: string) => {
    if (!value) return '未知时间'
    const d = dayjs(value)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : '未知时间'
  }

  const getInconsistencyLabel = (type: string) => {
    switch (type) {
      case 'vehicle_accounting_mismatch':
        return '\u8f66\u8f86\u7ba1\u7406\u5458\u4e0e\u4f1a\u8ba1\u5458\u4e0d\u4e00\u81f4'
      case 'vehicle_accounting_pending':
        return '\u4f1a\u8ba1\u5f85\u505a\u8d26'
      case 'accounting_cashier_mismatch':
        return '\u4f1a\u8ba1\u5458\u4e0e\u51fa\u7eb3\u5458\u4e0d\u4e00\u81f4'
      case 'overdue_vehicles':
        return '\u903e\u671f\u8f66\u8f86'
      default:
        return '\u5176\u4ed6\u5f02\u5e38'
    }
  }

  // 执行数据验证
  const performDataVerification = (roleProgress: RoleProgress): DataComparison => {
    const inconsistencies: DataComparison['inconsistencies'] = []

    // 车辆管理员 vs 会计员数据对比
    const vehicleVsAccounting = roleProgress.vehicleManager.completed === roleProgress.accountant.completed ? 'match' : 'mismatch'

    if (vehicleVsAccounting === 'mismatch') {
      const pendingCount = roleProgress.vehicleManager.completed - roleProgress.accountant.completed
      inconsistencies.push({
        type: pendingCount > 0 ? 'vehicle_accounting_pending' : 'vehicle_accounting_mismatch',
        description: pendingCount > 0
          ? `\u4f1a\u8ba1\u5f85\u505a\u8d26 ${pendingCount} \u7b14`
          : `\u8f66\u8f86\u7ba1\u7406\u5458\u786e\u8ba4\u6536\u6b3e ${roleProgress.vehicleManager.completed} \u7b14\uff0c\u4f1a\u8ba1\u5458\u5b8c\u6210\u505a\u8d26 ${roleProgress.accountant.completed} \u7b14`,
        severity: pendingCount > 0 ? 'low' : 'high'
      })
    }

    // 会计员 vs 出纳员数据对比
    const accountingVsCashier = roleProgress.accountant.completed === roleProgress.cashier.completed ? 'match' : 'mismatch'

    if (accountingVsCashier === 'mismatch') {
      inconsistencies.push({
        type: 'accounting_cashier_mismatch',
        description: `\u4f1a\u8ba1\u5458\u5b8c\u6210\u505a\u8d26 ${roleProgress.accountant.completed} \u7b14\uff0c\u51fa\u7eb3\u5458\u786e\u8ba4 ${roleProgress.cashier.completed} \u7b14`,
        severity: 'medium'
      })
    }

    if (roleProgress.vehicleManager.overdue > 0) {
      inconsistencies.push({
        type: 'overdue_vehicles',
        description: `\u53d1\u73b0 ${roleProgress.vehicleManager.overdue} \u8f86\u8f66\u8f86\u5df2\u903e\u671f`,
        severity: 'high'
      })
    }

    return {
      vehicleVsAccounting,
      accountingVsCashier,
      inconsistencies
    }
  }

  const generateReceiptActivities = (receipts: any[]): RecentActivity[] => {
    const activities: RecentActivity[] = []
    receipts
      .slice()
      .sort((a: any, b: any) => {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime()
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime()
        return tb - ta
      })
      .slice(0, 10)
      .forEach((r: any) => {
        if (r.status === 'accounted') {
          activities.push({
            id: `accounted_${r._id || r.id}`,
            type: 'accounted',
            user: '\u4f1a\u8ba1',
            description: `\u8bb0\u8d26\u5b8c\u6210\uff1a${r.plate || r.company || ''}${r.voucherNo ? `\uff08\u51ed\u8bc1\u53f7 ${r.voucherNo}\uff09` : ''}`,
            time: formatActivityTime(r.accountedAt || r.updatedAt || r.createdAt),
            icon: 'V'
          })
        } else if (r.status === 'cashier_done') {
          activities.push({
            id: `cashier_${r._id || r.id}`,
            type: 'cashier_done',
            user: '\u51fa\u7eb3',
            description: `\u6536\u6b3e\u786e\u8ba4\uff1a${r.plate || r.company || ''}`,
            time: formatActivityTime(r.verifiedAt || r.updatedAt || r.createdAt),
            icon: 'C'
          })
        } else if (r.status === 'submitted') {
          activities.push({
            id: `submitted_${r._id || r.id}`,
            type: 'submitted',
            user: '\u8f66\u8f86\u7ba1\u7406\u5458',
            description: `\u6536\u6b3e\u63d0\u4ea4\uff1a${r.plate || r.company || ''}`,
            time: formatActivityTime(r.createdAt || r.updatedAt),
            icon: 'S'
          })
        }
      })

    if (activities.length === 0) {
      return [{
        id: 'no_activity',
        type: 'system',
        user: '\u7cfb\u7edf',
        description: '\u6682\u65e0\u6700\u65b0\u6d3b\u52a8',
        time: '\u521a\u521a',
        icon: '-'
      }]
    }

    return activities.slice(0, 5)
  }

  const handleLogout = () => {
    Modal.confirm({
      title: '\u786e\u8ba4\u9000\u51fa',
      content: '\u786e\u5b9a\u8981\u9000\u51fa\u767b\u5f55\u5417\uff1f',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  // 导出数据
  const handleExportData = () => {
    Modal.info({
      title: '\u5bfc\u51fa\u6570\u636e',
      content: '\u6570\u636e\u5bfc\u51fa\u529f\u80fd\u5f00\u53d1\u4e2d\uff0c\u656c\u8bf7\u671f\u5f85\uff01',
    })
  }

  // 查看角色详情
  const handleViewRoleDetail = (role: string) => {
    const roleRoutes = {
      'vehicle_manager': '/vehicle-manager',
      'accountant': '/accountant',
      'cashier': '/cashier'
    }
    
    const route = roleRoutes[role as keyof typeof roleRoutes]
    if (route) {
      navigate(route)
    }
  }

  const handleSignRentAdjust = async (item: any) => {
    try {
      const res = await simpleDirectCloudApi.updateRentAdjustRequest(item._id || item.id, {
        status: 'approved',
        bossId: user?.id,
        bossName: user?.nickName,
        signedAt: new Date().toISOString()
      })
      if (res.success) {
        message.success('已签名')
        loadAllData()
      } else {
        message.error(res.error || '签名失败')
      }
    } catch (err) {
      console.error(err)
      message.error('签名失败')
    }
  }

  const pendingRentAdjust = rentAdjustRequests.filter((r: any) => r.status === 'pending')
  const filedRentAdjust = rentAdjustRequests.filter((r: any) => r.status === 'filed')
  const filedCountByVehicle = filedRentAdjust.reduce((acc: Record<string, number>, item: any) => {
    const key = String(item.vehicleId || item.plate || '')
    if (!key) return acc
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})
  const logCollections = Array.from(new Set(operationLogs.map((item: any) => String(item.collection || '').trim()).filter(Boolean)))
  const filteredOperationLogs = operationLogs.filter((item: any) => {
    if (logActionFilter !== 'all' && item.action !== logActionFilter) return false
    if (logCollectionFilter !== 'all' && String(item.collection || '') !== logCollectionFilter) return false
    const keyword = logKeyword.trim().toLowerCase()
    if (!keyword) return true
    const haystack = [
      item?.actor?.nickName,
      item?.actor?.username,
      item?.actor?.info,
      item?.collection,
      item?.targetId,
      item?.payloadSnapshot?.plate,
      item?.payloadSnapshot?.company,
      item?.payloadSnapshot?.vehicleId,
      item?.payloadSnapshot?.notes
    ].map(v => String(v || '').toLowerCase()).join(' ')
    return haystack.includes(keyword)
  })

  if (loading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', background: 'var(--bg)', minHeight: '100vh' }}>
      <BossHeaderCard user={user} onExport={handleExportData} onLogout={handleLogout} />
      <BossGlobalStats stats={globalStats} />
      <Card>
        <Tabs
          activeKey={activeView}
          onChange={setActiveView}
          items={[
            {
              key: 'overview',
              label: '\u603b\u89c8',
              children: (
                <BossOverviewTab
                  roleProgress={roleProgress}
                  onViewRoleDetail={handleViewRoleDetail}
                />
              )
            },
            {
              key: 'progress',
              label: '\u8fdb\u5ea6',
              children: (
                <BossProgressTab
                  recentActivities={recentActivities}
                  globalStats={globalStats}
                />
              )
            },
            {
              key: 'verification',
              label: '\u9a8c\u8bc1',
              children: (
                <BossVerificationTab
                  dataComparison={dataComparison}
                  receiptChecks={receiptChecks}
                  onOpenReceiptList={openReceiptList}
                  onOpenReceiptDetail={openReceiptDetail}
                />
              )
            },
            {
              key: 'rent-adjust',
              label: `租金更新申请 (${pendingRentAdjust.length})`,
              children: (
                <>
                  <Card size="small" style={{ marginBottom: 12 }}>
                    <Space>
                      <Tag color="orange">待核对 {pendingRentAdjust.length}</Tag>
                      <Tag color="blue">已归档申请单 {filedRentAdjust.length}</Tag>
                      <Text type="secondary">改租金后系统会自动产生待核对项，核对有无申请单后再签名通过。</Text>
                    </Space>
                  </Card>
                  <List
                    itemLayout="vertical"
                    dataSource={pendingRentAdjust}
                    locale={{ emptyText: '暂无申请' }}
                    renderItem={(item: any) => {
                      const matchCount = filedCountByVehicle[String(item.vehicleId || item.plate || '')] || 0
                      return (
                        <List.Item
                          actions={[
                            <Button key="sign" type="primary" onClick={() => handleSignRentAdjust(item)}>
                              核对完成并签名
                            </Button>
                          ]}
                        >
                          <List.Item.Meta
                            title={(
                              <span>
                                {item.plate || '-'} <Tag color="orange">待核对</Tag>
                              </span>
                            )}
                            description={(
                              <div>
                                <Row gutter={12}>
                                  <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company || '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">当前租金</Text> <Text>￥{Number(item.currentRent || 0).toFixed(2)}</Text></Col>
                                </Row>
                                <Row gutter={12}>
                                  <Col span={12}><Text type="secondary">申请后租金</Text> <Text>￥{Number(item.requestedRent || 0).toFixed(2)}</Text></Col>
                                  <Col span={12}>
                                    <Text type="secondary">匹配归档单</Text>{' '}
                                    <Tag color={matchCount > 0 ? 'green' : 'red'}>{matchCount} 份</Tag>
                                  </Col>
                                </Row>
                                {item.notes ? (
                                  <Row gutter={12}>
                                    <Col span={24}><Text type="secondary">申请说明</Text> <Text>{item.notes}</Text></Col>
                                  </Row>
                                ) : null}
                                <Row gutter={12}>
                                  <Col span={12}><Text type="secondary">申请人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">申请时间</Text> <Text>{item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                                </Row>
                              </div>
                            )}
                          />
                        </List.Item>
                      )
                    }}
                  />
                </>
              )
            },
            {
              key: 'vouchers',
              label: '\u51ed\u8bc1\u7ba1\u7406',
              children: (
                <BossVouchersTab
                  roleProgress={roleProgress}
                  allReceipts={allReceipts}
                  onOpenReceiptDetail={openReceiptDetail}
                />
              )
            },
            {
              key: 'operation-logs',
              label: `操作日志 (${filteredOperationLogs.length})`,
              children: (
                <>
                  <Card size="small" style={{ marginBottom: 12 }}>
                    <Row gutter={12}>
                      <Col flex="auto">
                        <Input
                          allowClear
                          placeholder="搜索：操作人 / 车牌 / 公司 / 目标ID / 备注"
                          value={logKeyword}
                          onChange={(e) => setLogKeyword(e.target.value)}
                        />
                      </Col>
                      <Col>
                        <Select
                          value={logActionFilter}
                          style={{ width: 120 }}
                          onChange={(value) => setLogActionFilter(value)}
                          options={[
                            { value: 'all', label: '全部动作' },
                            { value: 'create', label: '新增' },
                            { value: 'update', label: '更新' },
                            { value: 'delete', label: '删除' }
                          ]}
                        />
                      </Col>
                      <Col>
                        <Select
                          value={logCollectionFilter}
                          style={{ width: 180 }}
                          onChange={(value) => setLogCollectionFilter(value)}
                          options={[
                            { value: 'all', label: '全部集合' },
                            ...logCollections.map((item) => ({ value: item, label: item }))
                          ]}
                        />
                      </Col>
                    </Row>
                  </Card>
                  <List
                    itemLayout="vertical"
                    dataSource={filteredOperationLogs}
                    locale={{ emptyText: '暂无日志' }}
                    renderItem={(item: any) => (
                      <List.Item>
                        <List.Item.Meta
                          title={(
                            <Space>
                              <Tag color={item.action === 'create' ? 'green' : item.action === 'delete' ? 'red' : 'blue'}>
                                {item.action === 'create' ? '新增' : item.action === 'delete' ? '删除' : '更新'}
                              </Tag>
                              <Text strong>{item.collection || '-'}</Text>
                              <Text type="secondary">目标ID: {item.targetId || '-'}</Text>
                            </Space>
                          )}
                          description={(
                            <div>
                              <Row gutter={12}>
                                <Col span={8}>
                                  <Text type="secondary">操作人</Text>{' '}
                                  <Text>{item?.actor?.nickName || item?.actor?.username || '-'}</Text>
                                </Col>
                                <Col span={8}>
                                  <Text type="secondary">岗位</Text>{' '}
                                  <Text>{item?.actor?.info || '-'}</Text>
                                </Col>
                                <Col span={8}>
                                  <Text type="secondary">时间</Text>{' '}
                                  <Text>{item?.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>
                                </Col>
                              </Row>
                              <Row gutter={12}>
                                <Col span={8}>
                                  <Text type="secondary">车牌</Text>{' '}
                                  <Text>{item?.payloadSnapshot?.plate || '-'}</Text>
                                </Col>
                                <Col span={8}>
                                  <Text type="secondary">公司</Text>{' '}
                                  <Text>{item?.payloadSnapshot?.company || '-'}</Text>
                                </Col>
                                <Col span={8}>
                                  <Text type="secondary">状态</Text>{' '}
                                  <Text>{item?.payloadSnapshot?.status || '-'}</Text>
                                </Col>
                              </Row>
                            </div>
                          )}
                        />
                      </List.Item>
                    )}
                  />
                </>
              )
            }
          ]}
        />
      </Card>

      <BossReceiptDetailModal
        open={receiptDetailVisible}
        receiptDetail={receiptDetail}
        onClose={() => { setReceiptDetailVisible(false); setReceiptDetail(null) }}
      />
      <BossReceiptListModal
        open={receiptListVisible}
        receipts={allReceipts}
        onClose={() => setReceiptListVisible(false)}
        onOpenReceiptDetail={openReceiptDetail}
      />
    </div>
  )
}

export default BossPage





